import React from 'react';
import { Row, Col, Card, Table, Badge, Button, Spinner } from 'react-bootstrap';
import { Wallet, CheckCircle, AlertTriangle, Clock, TrendingUp, TrendingDown, Eye } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import KPICard from '../cards/KPICard';
import './CashierSection.css';

const CashierSection = ({ data, loading, onViewShift }) => {
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Đang tải dữ liệu tiền mặt...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-5">
        <p>Không có dữ liệu</p>
      </div>
    );
  }

  const { summary, problematic_shifts, voucher_statistics } = data;

  // Format currency
  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}k`;
    }
    return amount.toLocaleString('vi-VN');
  };

  // Transform voucher data for pie chart
  const voucherData = voucher_statistics?.map((item) => ({
    name: item.category,
    value: item.total_amount,
    count: item.voucher_count,
  })) || [];

  const COLORS = ['#52c41a', '#1890ff', '#faad14', '#f5222d', '#722ed1'];

  // Calculate percentages for cash flow
  const cashFlowTotal = summary?.cash_flow?.cash_in || 0;
  const cashInPercent = 100;
  const cashOutPercent = cashFlowTotal > 0 ? ((summary?.cash_flow?.cash_out / cashFlowTotal) * 100).toFixed(1) : 0;
  const netPercent = cashFlowTotal > 0 ? ((summary?.cash_flow?.net_cash_flow / cashFlowTotal) * 100).toFixed(1) : 0;

  // Get status config
  const getStatusConfig = (status) => {
    const configs = {
      BALANCED: { variant: 'success', icon: CheckCircle, text: 'Khớp số' },
      EXCESS: { variant: 'warning', icon: AlertTriangle, text: 'Thừa tiền' },
      SHORTAGE: { variant: 'danger', icon: AlertTriangle, text: 'Thiếu tiền' },
    };
    return configs[status] || configs.BALANCED;
  };

  // Get shift type icon
  const getShiftTypeIcon = (shiftType) => {
    const icons = {
      MORNING: '🌅',
      AFTERNOON: '🌆',
      EVENING: '🌃',
    };
    return icons[shiftType] || '📅';
  };

  return (
    <div className="cashier-section">


      {/* Summary KPI Cards */}
      <Row className="mb-4">
        <Col xs={6} sm={6} md={3} lg>
          <Card className="cashier-kpi-card mb-3">
            <Card.Body className="text-center">
              <div className="kpi-number">{summary?.total_shifts || 0}</div>
              <div className="kpi-label">Tổng ca</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} md={3} lg>
          <Card className="cashier-kpi-card danger mb-3">
            <Card.Body className="text-center">
              <TrendingDown size={20} className="mb-2" />
              <div className="kpi-number">{summary?.shifts_shortage || 0}</div>
              <div className="kpi-label">Thiếu tiền</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} md={3} lg>
          <Card className="cashier-kpi-card warning mb-3">
            <Card.Body className="text-center">
              <TrendingUp size={20} className="mb-2" />
              <div className="kpi-number">{summary?.shifts_excess || 0}</div>
              <div className="kpi-label">Thừa tiền</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} md={3} lg>
          <Card className="cashier-kpi-card info mb-3">
            <Card.Body className="text-center">
              <Clock size={20} className="mb-2" />
              <div className="kpi-number">{summary?.shifts_pending_reconciliation || 0}</div>
              <div className="kpi-label">Chờ đối soát</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Cash Flow Summary */}
      <Row className="mb-4">
        <Col xs={12}>
          <Card className="cash-flow-card">
            <Card.Header>
              <h5>💵 Dòng Tiền</h5>
            </Card.Header>
            <Card.Body>
              <div className="cash-flow-summary">
                <div className="cash-flow-item">
                  <div className="cash-flow-label">Tiền vào</div>
                  <div className="cash-flow-value success">
                    {formatCurrency(summary?.cash_flow?.cash_in || 0)}đ
                  </div>
                  <div className="cash-flow-percent">{cashInPercent}%</div>
                </div>
                <div className="cash-flow-arrow">→</div>
                <div className="cash-flow-item">
                  <div className="cash-flow-label">Tiền ra</div>
                  <div className="cash-flow-value danger">
                    {formatCurrency(summary?.cash_flow?.cash_out || 0)}đ
                  </div>
                  <div className="cash-flow-percent">{cashOutPercent}%</div>
                </div>
                <div className="cash-flow-arrow">=</div>
                <div className="cash-flow-item">
                  <div className="cash-flow-label">Ròng</div>
                  <div className="cash-flow-value primary">
                    {formatCurrency(summary?.cash_flow?.net_cash_flow || 0)}đ
                  </div>
                  <div className="cash-flow-percent">{netPercent}%</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Problematic Shifts Table and Voucher Chart */}
      <Row className="mb-4">
        <Col xs={12} lg={8}>
          <Card className="problematic-shifts-card">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5>⚠️ Ca Cần Xử Lý</h5>
              <Badge bg="secondary">{problematic_shifts?.length || 0} ca</Badge>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table hover className="problematic-shifts-table">
                  <thead>
                    <tr>
                      <th>Ca</th>
                      <th>Nhân viên</th>
                      <th>Chi nhánh</th>
                      <th>Thời gian</th>
                      <th className="text-end">Chênh lệch</th>
                      <th>Trạng thái</th>
                      <th className="text-center">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {problematic_shifts?.length > 0 ? (
                      problematic_shifts.map((shift) => {
                        const statusConfig = getStatusConfig(shift.status);
                        const StatusIcon = statusConfig.icon;
                        const isNegative = shift.difference < 0;

                        return (
                          <tr
                            key={shift.shift_id}
                            className={`shift-row ${shift.status.toLowerCase()} ${!shift.is_reconciled ? 'pending' : ''}`}
                          >
                            <td>
                              <span className="shift-type">
                                {getShiftTypeIcon(shift.shift_type)} {shift.shift_type}
                              </span>
                            </td>
                            <td>{shift.employee_name}</td>
                            <td>{shift.branch_name}</td>
                            <td>
                              <div className="shift-time">
                                {new Date(shift.start_time).toLocaleTimeString('vi-VN', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                                -
                                {new Date(shift.end_time).toLocaleTimeString('vi-VN', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                                <br />
                                <small>
                                  {new Date(shift.start_time).toLocaleDateString('vi-VN')}
                                </small>
                              </div>
                            </td>
                            <td className="text-end">
                              <span className={`difference ${isNegative ? 'negative' : 'positive'}`}>
                                {isNegative ? '-' : '+'}
                                {Math.abs(shift.difference).toLocaleString('vi-VN')}đ
                              </span>
                            </td>
                            <td>
                              <Badge bg={statusConfig.variant} className="d-flex align-items-center gap-1">
                                <StatusIcon size={14} />
                                {statusConfig.text}
                              </Badge>
                              {!shift.is_reconciled && (
                                <Badge bg="secondary" className="mt-1">
                                  <Clock size={12} /> Chờ đối soát
                                </Badge>
                              )}
                            </td>
                            <td className="text-center">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => onViewShift && onViewShift(shift.shift_id)}
                              >
                                <Eye size={16} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center">
                          Không có ca cần xử lý
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={4}>
          <Card className="voucher-chart-card">
            <Card.Header>
              <h5>📋 Phiếu Chi Theo Loại</h5>
            </Card.Header>
            <Card.Body>
              {voucherData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={voucherData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => entry.name}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {voucherData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${value.toLocaleString('vi-VN')}đ`} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="voucher-legend mt-3">
                    {voucherData.map((item, index) => (
                      <div key={index} className="voucher-legend-item">
                        <div
                          className="legend-color"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <div className="legend-text">
                          <div className="legend-name">{item.name}</div>
                          <div className="legend-value">
                            {formatCurrency(item.value)}đ ({item.count} phiếu)
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <p>Không có dữ liệu phiếu chi</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CashierSection;


